// src/pages/internal/clientesSwap/novoClienteSwap/index.js
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import {
	Grid,
	Box,
	Typography,
	TextField,
	Button,
	IconButton,
	Divider,
	useTheme, LinearProgress,
	Tooltip,
	FormControlLabel,
	Checkbox,
	Menu,
	MenuItem,
	ListItem, Dialog,
	DialogContent,
	DialogTitle,
	DialogActions,
	DialogContentText,
	Autocomplete,
	Chip,
	Switch,
	InputAdornment,
	Paper
} from "@mui/material";
import {
	DeleteOutline, InventoryOutlined,
	AccountTreeOutlined,
	EditNoteOutlined,
	InfoOutlined,
	Settings,
	AddOutlined,
	Add,
	MoreVert,
	CachedOutlined,
	RouteOutlined,
	Delete,
	Edit,
	ContactPhoneOutlined,
	SaveOutlined,
	BusinessOutlined,
	Search,
	Clear,
	KeyboardArrowUp,
	KeyboardArrowDown,
} from "@mui/icons-material";
import InputMask from "react-input-mask";
import dayjs from "dayjs";
import { LocalizationProvider, TimeField } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import useStyles from "./styles";
import api from "../../../../services/api";
import StatusTags from "../../../../components/uniqueTagSelect";
import DatagridComponent from "../../../../components/datagridComponent";
import ErpChip from "../../../../components/erp/ErpChip";
import InternoModalConfirmacaoSaida from "../../../../components/modalSaindoDaPagina";
import { removeMask, maskCellPhone, maskCnpj } from "../../../../utils/masks";

const DEFAUL_FORM = {
	nome: "",
	cnl: "",
	serviceId: "",
	options: [],
	swapTypes: [],
	distancia: "",
	quantidadeDeFibras: "",
	totalDeFibras: "",
	capacidade: "",
	urs: "",
	observacao: "",
	subclientes: [],
	clientesAfetados: []
};

const ModalEditCheckbox = ({
	open,
	onClose,
	onSave,
	title,
	value,
	onChange,
}) => {
	return (
		<Dialog open={open} onClose={onClose} sx={{
			'& .MuiPaper-root': {
				paddingBottom: '10px',
				borderRadius: "8px",
			},
		}}>
			<DialogTitle variant="mySubtitle">{title}</DialogTitle>
			<DialogContent>
				<DialogContentText>
					<TextField
						size="small"
						autoComplete="off"
						fullWidth
						value={value}
						onChange={onChange}
						sx={{ width: '300px' }}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault();
								if (value?.trim()) {
									onSave();
								}
							}
						}}
					/>
				</DialogContentText>
			</DialogContent>
			<DialogActions >
				<Button variant="outlined" onClick={onClose}>
					Cancelar
				</Button>
				<Button
					onClick={onSave}
					variant="contained"
					disabled={!value?.trim()}
				>
					Salvar
				</Button>
			</DialogActions>
		</Dialog >
	);
};

const EscalationContactsInlineSection = ({
	theme,
	styles,
	title,
	contacts = [],
	onOpen,
	openDisabled = false,
}) => {
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
				<Box sx={styles.boxContent}>
					<ContactPhoneOutlined color="primary" />
					<Typography variant="mySubtitle2">{title}</Typography>
				</Box>
				{typeof onOpen === "function" && (
					<Button
						variant="outlined"
						size="small"
						startIcon={<ContactPhoneOutlined />}
						onClick={onOpen}
						disabled={openDisabled}
					>
						Contatos de escalonamento
					</Button>
				)}
			</Box>

			<Box sx={{
				display: "flex", flexDirection: "column",
				backgroundColor: theme.palette.background.wallpaper || theme.palette.background.default,
				borderRadius: 4, p: 2, gap: 1, justifyContent: "center",
			}}>
				{contacts.length === 0 ? (
					<Typography variant="body2" color="textSecondary">
						Nenhum contato de escalonamento cadastrado.
					</Typography>
				) : (
					contacts.map((c, idx) => {
						const minutes = Number(c?.escalationTime) || 0;
						const hh = String(Math.floor(minutes / 60)).padStart(2, "0");
						const mm = String(minutes % 60).padStart(2, "0");
						return (
							<Box key={c?.id || idx} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "16px", px: 2, py: 1.5, backgroundColor: theme.palette.background.paper }}>
								<Grid container spacing={1}>
									<Grid item xs={12} sm={6} md={3}>
										<Typography variant="caption" color="textSecondary">Nome</Typography>
										<Typography variant="body2">{c?.name || "-"}</Typography>
									</Grid>
									<Grid item xs={12} sm={6} md={3}>
										<Typography variant="caption" color="textSecondary">Cargo</Typography>
										<Typography variant="body2">{c?.role || "-"}</Typography>
									</Grid>
									<Grid item xs={12} sm={6} md={3}>
										<Typography variant="caption" color="textSecondary">Telefone</Typography>
										<Typography variant="body2">{c?.phone ? maskCellPhone(c.phone) : "-"}</Typography>
									</Grid>
									<Grid item xs={12} sm={6} md={3}>
										<Typography variant="caption" color="textSecondary">Tempo de escalonamento</Typography>
										<Typography variant="body2">{`${hh}:${mm}`}</Typography>
									</Grid>
								</Grid>
							</Box>
						);
					})
				)}
			</Box>
		</Box>
	);
};

// Editor de contatos de escalonamento usado dentro do modal "Editar Parceiro/Cedente" (StatusTags).
// Recebe a tag em edição, gerencia seu próprio estado local e persiste via API.
// Registra `save` no parent via `registerSaver` para ser executado junto com o Salvar do modal.
function TagEscalationContactsEditor({
	tag,
	disabled = false,
	setSnackbar = () => { },
	onSaved = () => { },
	registerSaver = () => { },
}) {
	const theme = useTheme();
	const styles = useStyles(theme);

	const EMPTY_CONTACT = { id: "", name: "", role: "", phone: "", escalationTime: 0 };

	const [contacts, setContacts] = useState([]);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		setContacts((tag?.escalationContacts || []).map((c) => ({ ...c })));
	}, [tag?.id]); // eslint-disable-line react-hooks/exhaustive-deps

	const isDisabled = disabled || saving || !tag?.id;

	const handleAdd = () => setContacts((prev) => [...prev, { ...EMPTY_CONTACT }]);
	const handleRemove = (i) => setContacts((prev) => prev.filter((_, idx) => idx !== i));
	const handleChange = (i, field, value) =>
		setContacts((prev) => {
			const next = [...prev];
			next[i] = { ...next[i], [field]: value };
			return next;
		});

	const handleSave = async () => {
		if (!tag?.id) return;
		try {
			setSaving(true);
			const res = await api.post("/interno/upsert/tagEscalationContacts", {
				tagId: tag.id,
				contacts,
			});
			const saved = Array.isArray(res?.data) ? res.data : [];
			setContacts(saved.map((c) => ({ ...c })));
			onSaved(saved);
		} catch (error) {
			console.error("Falha ao salvar contatos de escalonamento:", error);
			setSnackbar({ children: "Falha ao salvar contatos", severity: "error" });
			throw error;
		} finally {
			setSaving(false);
		}
	};

	// Registra o saver para que seja executado pelo botão Salvar do modal pai.
	registerSaver(handleSave);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
				<Box sx={styles.boxContent}>
					<ContactPhoneOutlined color="primary" />
					<Typography variant="mySubtitle2">Contatos de Escalonamento</Typography>
				</Box>
				<Button
					variant="outlined"
					size="small"
					startIcon={<AddOutlined />}
					onClick={handleAdd}
					disabled={isDisabled}
				>
					Adicionar
				</Button>
			</Box>

			{contacts.length === 0 ? (
				<Typography variant="body2" color="textSecondary">
					Nenhum contato de escalonamento adicionado.
				</Typography>
			) : (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1, maxHeight: "45vh", overflowY: "auto", pr: 1 }}>
					{contacts.map((contato, index) => (
						<Box key={contato?.id || index} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "16px", px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 2, backgroundColor: theme.palette.background.paper }}>
							<Grid container spacing={1}>
								<Grid item xs={12} sm={6} md={3}>
									<TextField
										fullWidth
										size="small"
										autoComplete="off"
										label={`Nome do contato ${index + 1}`}
										value={contato?.name || ""}
										onChange={(e) => handleChange(index, "name", e.target.value)}
										disabled={isDisabled}
										sx={styles.textField}
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={3}>
									<TextField
										fullWidth
										size="small"
										autoComplete="off"
										label="Cargo"
										value={contato?.role || ""}
										onChange={(e) => handleChange(index, "role", e.target.value)}
										disabled={isDisabled}
										sx={styles.textField}
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={3}>
									<InputMask
										mask={removeMask(contato?.phone || "")?.length <= 10 ? "(99) 9999-99999" : "(99) 99999-9999"}
										value={contato?.phone || ""}
										onChange={(e) => handleChange(index, "phone", removeMask(e?.target?.value))}
										maskChar=""
										disabled={isDisabled}
									>
										{(props) => (
											<TextField
												{...props}
												fullWidth
												size="small"
												autoComplete="off"
												label="Telefone"
												disabled={isDisabled}
												sx={styles.textField}
											/>
										)}
									</InputMask>
								</Grid>
								<Grid item xs={12} sm={6} md={3}>
									<LocalizationProvider dateAdapter={AdapterDayjs}>
										<TimeField
											fullWidth
											size="small"
											label="Tempo de escalonamento (HH:mm)"
											format="HH:mm"
											disabled={isDisabled}
											value={
												contato?.escalationTime !== undefined && contato?.escalationTime !== null
													? dayjs().startOf("day").add(Number(contato.escalationTime) || 0, "minute")
													: null
											}
											onChange={(newValue) => {
												const minutes = newValue ? newValue.hour() * 60 + newValue.minute() : 0;
												handleChange(index, "escalationTime", minutes);
											}}
											sx={styles.textField}
										/>
									</LocalizationProvider>
								</Grid>
							</Grid>
							<IconButton color="error" onClick={() => handleRemove(index)} disabled={isDisabled}>
								<DeleteOutline />
							</IconButton>
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
}

export default forwardRef(function FormClientesSwap({
	onSave,
	editMode,
	setEditMode = () => { },
	createMode = false,
	updateOptions = () => { },
	loading,
	data = {},
	setSnackbar = () => { },
	setLoading = () => { },
	tiposDeSwap = {},
	serviceId = "",
	clientesSwap,
	setClientesSwap,
	formErrors = { nome: false, subclientes: [] },
	setFormErrors = () => { }
}, ref) {
	const theme = useTheme();
	const styles = useStyles(theme);

	const [selectedParceirosId, setSelectedParceirosId] = useState(null);
	const [selectedCedentesId, setSelectedCedentesId] = useState(null);

	// =========================================================================
	// CONTATOS DE ESCALONAMENTO (vinculados às Tags de Parceiro/Cedente)
	// =========================================================================
	const [parceiroContacts, setParceiroContacts] = useState([]);
	const [cedenteContacts, setCedenteContacts] = useState([]);

	// =========================================================================
	// LÓGICA DE INICIALIZAÇÃO E SINCRONIZAÇÃO
	// =========================================================================
	const [formData, setFormData] = useState({
		...DEFAUL_FORM,
		...(clientesSwap || {})
	});

	useEffect(() => {
		setFormData((prev) => {
			const merged = { ...prev };

			if (tiposDeSwap?.swapTypes?.length > 0) {
				const savedIds = [
					...(clientesSwap?.options || []).map(opt => opt.id),
					...(clientesSwap?.rlClientesSwapOptions || []).map(rl => rl.optionId)
				];

				merged.swapTypes = tiposDeSwap.swapTypes.map(masterOpt => {
					const masterId = masterOpt.id || masterOpt.optionId;
					const existingLocal = prev.swapTypes?.find(t => (t.id || t.optionId) === masterId);

					let isChecked = false;
					if (existingLocal && existingLocal.value !== undefined) {
						isChecked = existingLocal.value;
					} else {
						isChecked = savedIds.includes(masterId);
					}

					return { ...masterOpt, value: isChecked };
				});
			}

			if (JSON.stringify(prev.swapTypes) === JSON.stringify(merged.swapTypes)) {
				return prev;
			}
			return merged;
		});

		if (clientesSwap?.tags?.length > 0) {
			const parceiro = clientesSwap.tags.find(t => t.type === "parceiroSwap");
			const cedente = clientesSwap.tags.find(t => t.type === "cedenteSwap");

			// Prioriza os IDs já persistidos no estado pai (sobrevivem à troca de aba/desmontagem)
			const parceiroId = clientesSwap?.parceiroSwapId ?? parceiro?.id ?? null;
			const cedenteId = clientesSwap?.cedenteSwapId ?? cedente?.id ?? null;

			setSelectedParceirosId(prev => prev !== parceiroId ? parceiroId : prev);
			setSelectedCedentesId(prev => prev !== cedenteId ? cedenteId : prev);
		} else if (clientesSwap?.parceiroSwapId || clientesSwap?.cedenteSwapId) {
			// Sem `tags` carregadas, mas há IDs persistidos no estado pai
			setSelectedParceirosId(prev => prev !== (clientesSwap?.parceiroSwapId ?? null) ? (clientesSwap?.parceiroSwapId ?? null) : prev);
			setSelectedCedentesId(prev => prev !== (clientesSwap?.cedenteSwapId ?? null) ? (clientesSwap?.cedenteSwapId ?? null) : prev);
		} else if (!clientesSwap) {
			setSelectedParceirosId(null);
			setSelectedCedentesId(null);
		}
	}, [tiposDeSwap, clientesSwap?.id]);

	useEffect(() => {
		if (typeof setClientesSwap === "function") {
			setClientesSwap((prev) => {
				const nextState = {
					...prev,
					...formData,
					parceiroSwapId: selectedParceirosId,
					cedenteSwapId: selectedCedentesId
				};

				if (JSON.stringify(prev) === JSON.stringify(nextState)) {
					return prev;
				}
				return nextState;
			});
		}
	}, [formData, selectedParceirosId, selectedCedentesId, setClientesSwap]);

	const handleSwapTypeCheckboxChange = (index, checked) => {
		setFormData((prev) => {
			const novosSwapTypes = [...prev.swapTypes];
			novosSwapTypes[index] = { ...novosSwapTypes[index], value: checked };
			return { ...prev, swapTypes: novosSwapTypes };
		});
	};

	const [prevData, setPrevData] = useState(
		JSON.parse(
			JSON.stringify({
				...DEFAUL_FORM,
				...tiposDeSwap,
				...data,
				clientServiceId: serviceId,
			}),
		),
	);

	const [loadingDeliveryTypes, setLoadingDeliveryTypes] = useState(false);
	const [loadingTags, setLoadingTags] = useState(false);
	const [parceirosTags, setParceirosTags] = useState([]);
	const [cedentesTags, setCedentesTags] = useState([]);
	const parceirosTagRef = useRef(null);
	const cedentesTagRef = useRef(null);
	// =========================================================================
	// CLIENTES AFETADOS
	// =========================================================================
	const [clientesDisponiveis, setClientesDisponiveis] = useState([]);
	const [loadingClientesAfetados, setLoadingClientesAfetados] = useState(false);
	const [clientesAfetadosFilter, setClientesAfetadosFilter] = useState("");
	const [showOnlySelectedClientes, setShowOnlySelectedClientes] = useState(false);
	const selectedClientesAfetados = Array.isArray(formData.clientesAfetados) ? formData.clientesAfetados : [];
	const selectedClientesAfetadosIds = useMemo(
		() => new Set(selectedClientesAfetados.map(c => c?.id).filter(Boolean)),
		[selectedClientesAfetados]
	);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				setLoadingClientesAfetados(true);
				const res = await api.get("/interno/getClients");
				// Grupos econômicos não participam de swap — exclui das options
				if (!cancelled) {
					setClientesDisponiveis(
						Array.isArray(res?.data)
							? res.data.filter((cliente) => !cliente?.isGrupoEconomico)
							: []
					);
				}
			} catch (err) {
				console.error("Falha ao buscar clientes:", err);
				if (!cancelled) {
					setSnackbar?.({ children: "Falha ao buscar clientes para 'Clientes Afetados'", severity: "error" });
				}
			} finally {
				if (!cancelled) setLoadingClientesAfetados(false);
			}
		})();
		return () => { cancelled = true; };
	}, []);

	useEffect(() => {
		const incoming = clientesSwap?.clientesAfetados;
		if (!Array.isArray(incoming)) return;
		setFormData(prev => {
			const prevList = Array.isArray(prev.clientesAfetados) ? prev.clientesAfetados : [];
			const sameLen = prevList.length === incoming.length;
			const sameIds = sameLen && incoming.every((c, i) => c?.id === prevList[i]?.id);
			if (sameIds) return prev;
			return { ...prev, clientesAfetados: incoming.map(c => ({ ...c })) };
		});
	}, [clientesSwap?.id, clientesSwap?.clientesAfetados]);

	const handleClientesAfetadosChange = (_, value) => {
		const seen = new Set();
		const dedup = (value || []).filter(c => {
			if (!c?.id || seen.has(c.id)) return false;
			seen.add(c.id);
			return true;
		});
		setFormData(prev => ({ ...prev, clientesAfetados: dedup }));
	};

	const toggleClienteAfetado = (cliente, checked) => {
		if (!cliente?.id) return;
		setFormData(prev => {
			const current = Array.isArray(prev.clientesAfetados) ? prev.clientesAfetados : [];
			if (checked) {
				if (current.some(c => c?.id === cliente.id)) return prev;
				return { ...prev, clientesAfetados: [...current, cliente] };
			}
			return { ...prev, clientesAfetados: current.filter(c => c?.id !== cliente.id) };
		});
	};

	const clientesAfetadosRows = useMemo(() => {
		const term = clientesAfetadosFilter.trim().toLowerCase();
		const base = Array.isArray(clientesDisponiveis) ? clientesDisponiveis : [];
		const scoped = showOnlySelectedClientes
			? base.filter(c => selectedClientesAfetadosIds.has(c?.id))
			: base;
		if (!term) return scoped;
		return scoped.filter(c => {
			const nome = (c?.nome || "").toLowerCase();
			const cn = (c?.cnpj || "").toLowerCase();
			const erp = String(c?.clienteErpId || "").toLowerCase();
			const nomeErp = `${c?.erp?.nome || ""} ${c?.erp?.tipo || ""}`.toLowerCase();
			return nome.includes(term) || cn.includes(term) || erp.includes(term) || nomeErp.includes(term);
		});
	}, [clientesDisponiveis, clientesAfetadosFilter, showOnlySelectedClientes, selectedClientesAfetadosIds]);

	const clientesAfetadosColumns = useMemo(() => ([
		{
			field: "clienteErpId",
			headerName: "Código",
			width: 110,
			renderCell: (params) => params.row?.clienteErpId ?? "-",
		},
		// O código se repete entre ERPs: o selo diz de qual ERP é o cliente.
		{
			field: "erp",
			headerName: "ERP",
			width: 110,
			sortable: false,
			renderCell: (params) => <ErpChip erp={params.row?.erp} />,
		},
		{
			field: "cliente",
			headerName: "Cliente",
			flex: 1,
			minWidth: 200,
			renderCell: (params) => params.row?.nome || "-",
		},
		{
			field: "cnpj",
			headerName: "CNPJ",
			width: 180,
			renderCell: (params) => params.row?.cnpj ? maskCnpj(params.row.cnpj) : "-",
		},
		{
			field: "selecionado",
			headerName: "Selecionar",
			width: 110,
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			renderCell: (params) => (
				<Switch
					size="small"
					checked={selectedClientesAfetadosIds.has(params.row?.id)}
					onChange={(e) => toggleClienteAfetado(params.row, e.target.checked)}
					disabled={!(editMode || createMode) || loading}
					onClick={(e) => e.stopPropagation()}
				/>
			),
		},
	]), [selectedClientesAfetadosIds, editMode, createMode, loading]);
	const [selectedFieldItem, setSelectedFieldItem] = useState(null);
	const [anchorActionMenu, setAnchorActionMenu] = useState(null);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [editingField, setEditingField] = useState(null);
	const [editingIndex, setEditingIndex] = useState(null);
	const [editedLabel, setEditedLabel] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);

	useEffect(() => {
		setParceirosTags(null);
		setCedentesTags(null);
		getTags();
	}, []);

	const handleReset = () => {
		setFormData(JSON.parse(JSON.stringify(prevData)));
		setFormErrors({ nome: false, subclientes: [] });
		setEditMode(false);
	};

	useImperativeHandle(ref, () => ({
		handleReset,
		handleSave,
	}));

	async function handleSave() { }

	const handleMenuClick = (event) => {
		setEditingField(event.currentTarget.name);
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	// =========================================================================
	// HANDLERS DE INPUTS COM REGEX
	// =========================================================================

	// Campos de Texto Comuns
	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));

		// Limpa o erro ao digitar
		if (name === "nome" && value.trim() !== "") {
			setFormErrors(prev => ({ ...prev, nome: false }));
		}
	};

	// Campos Numéricos (Float) - Bloqueia letras via Regex (permitindo apenas um separador)
	const handleFloatChange = (e) => {
		const { name, value } = e.target;
		// ^\d*([.,]\d*)?$ aceita string vazia, ou números, limitando a 1 separador (ponto ou virgula)
		if (value === '' || /^\d*([.,]\d*)?$/.test(value)) {
			setFormData((prev) => ({ ...prev, [name]: value.replace(',', '.') }));
		}
	};

	// Campos Numéricos (Inteiro) - Bloqueia letras via Regex
	const handleIntChange = (e) => {
		const { name, value } = e.target;
		if (value === '' || /^\d*$/.test(value)) {
			setFormData((prev) => ({ ...prev, [name]: value }));
		}
	};

	// Subtrechos
	const handleSubclienteChange = (index, field, value) => {
		const novosSubclientes = [...formData.subclientes];
		novosSubclientes[index][field] = value;
		setFormData((prev) => ({ ...prev, subclientes: novosSubclientes }));

		if (field === "nome" && value.trim() !== "") {
			setFormErrors(prev => ({
				...prev,
				subclientes: prev.subclientes.filter(errIndex => errIndex !== index)
			}));
		}
	};

	const handleAddSubcliente = () => {
		setFormData((prev) => ({
			...prev,
			subclientes: [
				...prev.subclientes,
				{ nome: "", cnl: "", posicao: prev.subclientes.length + 1 }
			]
		}));
	};

	const handleRemoveSubcliente = (indexToRemove) => {
		setFormData((prev) => {
			const listFiltada = prev.subclientes.filter((_, index) => index !== indexToRemove);
			const listaReordenada = listFiltada.map((sub, i) => ({ ...sub, posicao: i + 1 }));
			return { ...prev, subclientes: listaReordenada };
		});

		// Reajusta os índices dos erros para os itens que sobram
		setFormErrors(prev => ({
			...prev,
			subclientes: prev.subclientes
				.filter(errIndex => errIndex !== indexToRemove)
				.map(errIndex => errIndex > indexToRemove ? errIndex - 1 : errIndex)
		}));
	};

	const handleMoveSubcliente = (fromIndex, direction) => {
		const toIndex = fromIndex + direction;
		setFormData((prev) => {
			const lista = [...prev.subclientes];
			if (toIndex < 0 || toIndex >= lista.length) return prev;
			[lista[fromIndex], lista[toIndex]] = [lista[toIndex], lista[fromIndex]];
			const reordenada = lista.map((sub, i) => ({ ...sub, posicao: i + 1 }));
			return { ...prev, subclientes: reordenada };
		});

		// Acompanha os índices dos erros ao trocar posição
		setFormErrors((prev) => ({
			...prev,
			subclientes: (prev.subclientes || []).map((errIndex) => {
				if (errIndex === fromIndex) return toIndex;
				if (errIndex === toIndex) return fromIndex;
				return errIndex;
			}),
		}));
	};

	const handleTagUpdate = async (updatedTag) => {
		try {
			await api.post("/interno/update/tagsCircuito", updatedTag);
			// Mantém o estado pai sincronizado para que o StatusTags não reverta
			// a edição via useEffect([initialTags]) em re-renders subsequentes.
			setParceirosTags((prev) => (prev || []).map((t) => (t?.id === updatedTag?.id ? { ...t, ...updatedTag } : t)));
			setCedentesTags((prev) => (prev || []).map((t) => (t?.id === updatedTag?.id ? { ...t, ...updatedTag } : t)));
		} catch (error) {
			throw error;
		}
	};

	const handleTagDelete = async (tagId) => {
		try {
			await api.post("/interno/delete/tagsCircuito", { id: tagId });
			setParceirosTags((prev) => (prev || []).filter((t) => t?.id !== tagId));
			setCedentesTags((prev) => (prev || []).filter((t) => t?.id !== tagId));
		} catch (error) {
			throw error;
		}
	};

	const handleParceirosSelect = (tagId) => {
		try {
			setSelectedParceirosId(tagId);
		} catch (error) {
			console.error("Erro ao selecionar tag:", error);
		}
	};

	const handleParceirosCreate = async (newTag) => {
		try {
			const response = await api.post("/interno/create/tagsCircuito", {
				...newTag,
				type: "parceiroSwap",
			});
			const created = response?.data;
			// Adiciona ao estado pai para que o StatusTags não perca a tag recém-criada
			// quando o useEffect([initialTags]) for disparado por outras atualizações.
			if (created?.id) {
				setParceirosTags((prev) => {
					const list = prev || [];
					return list.some((t) => t?.id === created.id) ? list : [...list, created];
				});
			}
			return created;
		} catch (error) {
			throw error;
		}
	};

	const handleCedentesSelect = (tagId) => {
		try {
			setSelectedCedentesId(tagId);
		} catch (error) {
			console.error("Erro ao selecionar tag:", error);
		}
	};

	const handleCedentesCreate = async (newTag) => {
		try {
			const response = await api.post("/interno/create/tagsCircuito", {
				...newTag,
				type: "cedenteSwap",
			});
			const created = response?.data;
			if (created?.id) {
				setCedentesTags((prev) => {
					const list = prev || [];
					return list.some((t) => t?.id === created.id) ? list : [...list, created];
				});
			}
			return created;
		} catch (error) {
			throw error;
		}
	};

	const handleOpenEditModal = () => {
		setEditedLabel(formData?.[editingField][editingIndex]?.label);
		setEditModalOpen(true);
		handleActionMenuClose();
	};

	async function getTags() {
		try {
			setLoadingTags(true);
			const promises = [
				api.post("/interno/findMany/tagsCircuito", { type: "parceiroSwap" }),
				api.post("/interno/findMany/tagsCircuito", { type: "cedenteSwap" }),
			];
			const [parceiros, cedentes,] = await Promise.all(promises);

			setParceirosTags(parceiros?.data);
			setCedentesTags(cedentes?.data);

		} catch (error) {
			console.error("Falha ao obter tags:", error);
			setSnackbar({
				children: `Falha ao obter tags`,
				severity: "error",
			});
		} finally {
			setLoadingTags(false);
		}
	}

	// =========================================================================
	// SYNC CONTATOS DE ESCALONAMENTO QUANDO TROCA A TAG OU A LISTA ATUALIZA
	// =========================================================================
	useEffect(() => {
		const tag = (parceirosTags || []).find((t) => t?.id === selectedParceirosId);
		const list = tag?.escalationContacts || [];
		setParceiroContacts(list.map((c) => ({ ...c })));
	}, [selectedParceirosId, parceirosTags]);

	useEffect(() => {
		const tag = (cedentesTags || []).find((t) => t?.id === selectedCedentesId);
		const list = tag?.escalationContacts || [];
		setCedenteContacts(list.map((c) => ({ ...c })));
	}, [selectedCedentesId, cedentesTags]);

	async function handleRemoveCheckbox() {
		try {
			setLoadingDeliveryTypes(true);
			setLoading(true);
			await api.post("/interno/delete/opcaoCircuito", {
				id: selectedFieldItem?.optionId || selectedFieldItem?.id,
			});
			setFormData((prev) => ({
				...prev,
				[editingField]: prev?.[editingField]?.filter(
					(_, i) => i !== selectedFieldItem?.index,
				),
			}));
			setSearchTerm("");
			setOpenDeleteModal(false);
			setSnackbar({
				children: "Seletor removido com sucesso",
				severity: "success",
			});
			updateOptions();
		} catch (error) {
			console.error(error);
			setSnackbar({
				children: "Falha ao remover seletor",
				severity: "error",
			});
		} finally {
			setLoading(false);
			setLoadingDeliveryTypes(false);
		}
	}

	const exactMatch = formData?.[editingField]?.some(
		(type) => type?.label?.toLowerCase() === searchTerm?.toLowerCase(),
	);

	async function handleAddCeckbox(newLabel) {
		try {
			setLoadingDeliveryTypes(true);
			setLoading(true);
			await api.post("/interno/create/opcaoCircuito", {
				label: newLabel,
				type: `swapTypes`,
			});

			setSearchTerm("");
			setSnackbar({
				children: "Seletor criado com sucesso",
				severity: "success",
			});
			updateOptions();
		} catch (error) {
			console.error(error);
			setSnackbar({
				children: "Falha ao criar seletor",
				severity: "error",
			});
		} finally {
			setLoading(false);
			setLoadingDeliveryTypes(false);
		}
	}

	async function handleSaveEditCheckbox() {
		try {
			setLoadingDeliveryTypes(true);
			setLoading(true);
			if (
				editedLabel?.trim() &&
				!formData?.[editingField]?.some(
					(type) =>
						type?.label?.toLowerCase() === editedLabel?.trim()?.toLowerCase(),
				)
			) {
				await api.post("/interno/update/opcaoCircuito", {
					id: selectedFieldItem?.optionId || selectedFieldItem?.id,
					label: editedLabel,
				});

				setEditModalOpen(false);
				setSnackbar({
					children: "Seletor atualizado com sucesso",
					severity: "success",
				});
				updateOptions();
			} else {
				throw new Error("Titulo precisa ser único");
			}
		} catch (error) {
			console.error(error);
			if (error?.message === "Titulo precisa ser único") {
				setSnackbar({
					children: "Titulo precisa ser único",
					severity: "error",
				});
			} else {
				setSnackbar({
					children: "Falha ao editar seletor",
					severity: "error",
				});
			}
		} finally {
			setLoading(false);
			setLoadingDeliveryTypes(false);
		}
	}

	const handleActionMenuClick = (event, index) => {
		event.stopPropagation();
		setAnchorActionMenu(event.currentTarget);
		setEditingIndex(index);
	};

	const handleActionMenuClose = () => {
		setAnchorActionMenu(null);
		setEditingIndex(null);
	};

	const filteredDeliveryTypes = formData?.[editingField]?.filter((type) =>
		type?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase()),
	);

	return (
		<Box component="form" onSubmit={(e) => e.preventDefault()} sx={{ p: 2 }}>
			<Box sx={{ ...styles.boxContent, mb: 2 }}>
				<RouteOutlined color="primary" />
				<Typography variant="mySubtitle2">{editMode ? "Editar Trecho" : "Adicionar Trecho"}</Typography>
			</Box>

			<Grid container spacing={2}>
				<Grid item xs={12} sm={4}>
					<TextField
						fullWidth
						label="Trecho"
						name="nome"
						value={formData.nome}
						onChange={handleChange}
						required
						size="small"
						sx={styles.textField}
						error={formErrors.nome}
						helperText={formErrors.nome ? "Nome do trecho é obrigatório" : ""}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<TextField fullWidth label="CNL" name="cnl" value={formData.cnl} onChange={handleChange} size="small" sx={styles.textField} />
				</Grid>
			</Grid>
			<Divider sx={{ my: 3 }} />

			<Box sx={{ ...styles.boxContent, mb: 2 }}>
				<CachedOutlined color="primary" />
				<Typography variant="mySubtitle2">Relação de sessão</Typography>
			</Box>

			<Grid container spacing={2}>
				<Grid item xs={12} sm={4}>
					<StatusTags
						ref={parceirosTagRef}
						initialTags={parceirosTags}
						selectedTagId={selectedParceirosId}
						onTagSelect={handleParceirosSelect}
						onTagCreate={handleParceirosCreate}
						onTagUpdate={handleTagUpdate}
						onTagDelete={handleTagDelete}
						texts={{ label: "Parceiro", placeholder: "Selecionar Parceiro", searchPlaceholder: "Buscar ou criar Parceiro", editTitle: "Editar Parceiro", deleteTitle: "Excluir Parceiro", deleteConfirmation: "Tem certeza que deseja excluir o Parceiro", nameLabel: "Nome do Parceiro", colorLabel: "Cor", cancelButton: "Cancelar", saveButton: "Salvar", deleteButton: "Excluir", createButton: 'Criar "{tag}"' }}
						loading={loadingTags}
						disabled={!(editMode || createMode)}
						renderEditModalExtraContent={({ tag, registerSaver }) => (
							<TagEscalationContactsEditor
								tag={tag}
								disabled={loading}
								setSnackbar={setSnackbar}
								registerSaver={registerSaver}
								onSaved={(savedContacts) => {
									setParceirosTags((prev) =>
										(prev || []).map((t) =>
											t?.id === tag?.id ? { ...t, escalationContacts: savedContacts } : t
										)
									);
								}}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<StatusTags
						ref={cedentesTagRef}
						initialTags={cedentesTags}
						selectedTagId={selectedCedentesId}
						onTagSelect={handleCedentesSelect}
						onTagCreate={handleCedentesCreate}
						onTagUpdate={handleTagUpdate}
						onTagDelete={handleTagDelete}
						texts={{ label: "Cedentes", placeholder: "Selecionar Cedentes", searchPlaceholder: "Buscar ou criar Cedentes", editTitle: "Editar Cedentes", deleteTitle: "Excluir Cedentes", deleteConfirmation: "Tem certeza que deseja excluir a Cedentes", nameLabel: "Nome da Cedentes", colorLabel: "Cor", cancelButton: "Cancelar", saveButton: "Salvar", deleteButton: "Excluir", createButton: 'Criar "{tag}"' }}
						loading={loadingTags}
						disabled={!(editMode || createMode)}
						renderEditModalExtraContent={({ tag, registerSaver }) => (
							<TagEscalationContactsEditor
								tag={tag}
								disabled={loading}
								setSnackbar={setSnackbar}
								registerSaver={registerSaver}
								onSaved={(savedContacts) => {
									setCedentesTags((prev) =>
										(prev || []).map((t) =>
											t?.id === tag?.id ? { ...t, escalationContacts: savedContacts } : t
										)
									);
								}}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<TextField fullWidth label="Serviço ID" name="serviceId" value={formData.serviceId} onChange={handleChange} size="small" sx={styles.textField} />
				</Grid>
			</Grid>

			{/* --- CONTATOS DE ESCALONAMENTO (Parceiro / Cedente) - exibição read-only no form --- */}
			{(selectedParceirosId || selectedCedentesId) && (
				<>
					<Divider sx={{ my: 3 }} />
					{selectedParceirosId && (
						<EscalationContactsInlineSection
							theme={theme}
							styles={styles}
							title="Contatos de Escalonamento — Parceiro"
							contacts={parceiroContacts}
							onOpen={() => parceirosTagRef.current?.openEditModal?.(selectedParceirosId)}
							openDisabled={!(editMode || createMode) || loading}
						/>
					)}
					{selectedCedentesId && (
						<Box sx={{ mt: selectedParceirosId ? 2 : 0 }}>
							<EscalationContactsInlineSection
								theme={theme}
								styles={styles}
								title="Contatos de Escalonamento — Cedente"
								contacts={cedenteContacts}
								onOpen={() => cedentesTagRef.current?.openEditModal?.(selectedCedentesId)}
								openDisabled={!(editMode || createMode) || loading}
							/>
						</Box>
					)}
				</>
			)}

			<Divider sx={{ my: 3 }} />

			{/* --- TIPOS DE SWAP --- */}
			<Grid item xs={12}>
				<Box sx={styles.boxFieldWithConfig}>
					{!!loadingDeliveryTypes && (
						<LinearProgress sx={{ ...styles.linearProgress, mt: -2, left: 0 }} />
					)}
					<Box sx={styles.titleWithConfig}>
						<Box sx={{ ...styles.boxContent }}>
							<InventoryOutlined color="primary" />
							<Typography variant="mySubtitle2">Tipos de Swap</Typography>
						</Box>
						<Tooltip title="Tipos de Swap: Canal Alien, Colocation, Construção Conjunta, entre outras." arrow>
							<InfoOutlined sx={{ color: theme.palette.tertiary?.light || 'action.active', fontSize: 18, cursor: "pointer", mb: 0.6 }} />
						</Tooltip>
					</Box>
					<IconButton
						name="swapTypes"
						sx={{ my: -1 }}
						onClick={handleMenuClick}
						disabled={!(editMode || createMode) || loading}
					>
						<Settings />
					</IconButton>
				</Box>
			</Grid>

			<Grid container spacing={1} sx={styles.contentIdentation}>
				{!formData?.swapTypes?.length ? (
					<Grid item xs={12}>
						<Typography variant="mySubtitle2" color={theme?.palette?.colors?.gray?.medium}>
							As opções de tipo de swap serão exibidas aqui. É necessário
							criar clicando no ícone de configuração (<Settings sx={styles.noDeliveryFound} />).
						</Typography>
					</Grid>
				) : (
					formData?.swapTypes?.map((type, index) => (
						<Grid item xs={12} sm={6} md={4} lg={3} key={type?.id || type?.label || index} sx={styles.formControlItems}>
							<FormControlLabel
								disabled={!(editMode || createMode) || loading}
								sx={styles.limitWidthCheckbox}
								control={
									<Checkbox
										checked={!!type?.value}
										onChange={(e) => handleSwapTypeCheckboxChange(index, e.target.checked)}
									/>
								}
								label={type?.label}
							/>
						</Grid>
					))
				)}
			</Grid>

			<Divider sx={{ my: 3 }} />

			{/* --- DETALHES TÉCNICOS --- */}
			<Grid container spacing={2}>
				<Grid item xs={12} sm={4} md={2.4}>
					<TextField
						fullWidth
						type="text"
						inputMode="decimal"
						label="Distância (km)"
						name="distancia"
						value={formData.distancia}
						onChange={handleFloatChange}
						size="small"
						sx={styles.textField}
					/>
				</Grid>
				<Grid item xs={12} sm={4} md={2.4}>
					<TextField
						fullWidth
						type="text"
						inputMode="numeric"
						label="Qtd. de Fibras"
						name="quantidadeDeFibras"
						value={formData.quantidadeDeFibras}
						onChange={handleIntChange}
						size="small"
						sx={styles.textField}
					/>
				</Grid>
				<Grid item xs={12} sm={4} md={2.4}>
					<TextField
						fullWidth
						type="text"
						inputMode="numeric"
						label="Total Fibras (km)"
						name="totalDeFibras"
						value={formData.totalDeFibras}
						onChange={handleIntChange}
						size="small"
						sx={styles.textField}
					/>
				</Grid>
				<Grid item xs={12} sm={4} md={2.4}>
					<TextField
						fullWidth
						type="text"
						inputMode="decimal"
						label="Capacidade (Gbps)"
						name="capacidade"
						value={formData.capacidade}
						onChange={handleFloatChange}
						size="small"
						sx={styles.textField}
					/>
				</Grid>
				<Grid item xs={12} sm={4} md={2.4}>
					<TextField
						fullWidth
						label="UR's"
						name="urs"
						value={formData.urs}
						onChange={handleChange}
						size="small"
						sx={styles.textField}
					/>
				</Grid>
			</Grid>
			<Divider sx={{ my: 3 }} />
			{!clientesSwap?.parentId && (
				<>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
						<Box sx={styles.boxContent}>
							<AccountTreeOutlined color="primary" />
							<Typography variant="mySubtitle2">Subtrechos</Typography>
						</Box>
						<Button variant="outlined" size="small" startIcon={<AddOutlined />} onClick={handleAddSubcliente}>
							Adicionar Subtrecho
						</Button>
					</Box>
					<Box sx={{
						display: 'flex', flexDirection: 'column',
						backgroundColor: theme.palette.background.wallpaper || theme.palette.background.default,
						borderRadius: 4, p: 2, gap: 1, justifyContent: "center",
					}}>
						{formData.subclientes.length === 0 ? (
							<Typography variant="body2" color="textSecondary" >
								Nenhum subtrecho adicionado.
							</Typography>
						) : (
							formData.subclientes.map((sub, index) => (
								<Box key={index} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "16px", px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, backgroundColor: theme.palette.background.paper }}>
									<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 36 }}>
										<IconButton
											size="small"
											onClick={() => handleMoveSubcliente(index, -1)}
											disabled={index === 0}
											title="Mover para cima"
											sx={{ p: 0.25 }}
										>
											<KeyboardArrowUp fontSize="small" />
										</IconButton>
										<IconButton
											size="small"
											onClick={() => handleMoveSubcliente(index, 1)}
											disabled={index === formData.subclientes.length - 1}
											title="Mover para baixo"
											sx={{ p: 0.25 }}
										>
											<KeyboardArrowDown fontSize="small" />
										</IconButton>
									</Box>
									<Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: '80px' }}>
										Posição: {sub.posicao}
									</Typography>
									<TextField
										fullWidth
										label="Nome do Subtrecho"
										value={sub.nome}
										onChange={(e) => handleSubclienteChange(index, "nome", e.target.value)}
										size="small"
										required
										sx={styles.textField}
										error={formErrors.subclientes?.includes(index)}
										helperText={formErrors.subclientes?.includes(index) ? "Nome do subtrecho é obrigatório" : ""}
									/>
									<TextField fullWidth label="CNL" value={sub.cnl} onChange={(e) => handleSubclienteChange(index, "cnl", e.target.value)} size="small" sx={styles.textField} />
									<IconButton color="error" onClick={() => handleRemoveSubcliente(index)}>
										<DeleteOutline />
									</IconButton>
								</Box>
							))
						)}
					</Box>
				</>
			)}
			<Divider sx={{ my: 3 }} />

			{/* --- CLIENTES AFETADOS --- */}
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
				<Box sx={styles.boxContent}>
					<BusinessOutlined color="primary" />
					<Typography variant="mySubtitle2">Clientes Afetados</Typography>
					<Tooltip title="Ative o switch para marcar os clientes impactados por este trecho." arrow>
						<InfoOutlined sx={{ color: theme.palette.tertiary?.light || 'action.active', fontSize: 18, cursor: "pointer", mb: 0.6 }} />
					</Tooltip>
					<Chip
						size="small"
						color="primary"
						variant="outlined"
						label={`${selectedClientesAfetados.length} selecionado(s)`}
						sx={{ ml: 1 }}
					/>
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
					<Tooltip title="Mostrar apenas selecionados" arrow>
						<FormControlLabel
							control={
								<Switch
									size="small"
									checked={showOnlySelectedClientes}
									onChange={(e) => setShowOnlySelectedClientes(e.target.checked)}
									disabled={selectedClientesAfetados.length === 0}
								/>
							}
							label={<Typography variant="body2">Selecionados</Typography>}
							sx={{ m: 0 }}
						/>
					</Tooltip>
					<TextField
						label="Buscar cliente"
						variant="outlined"
						autoComplete="off"
						placeholder="Cliente"
						size="small"
						value={clientesAfetadosFilter}
						onChange={(e) => setClientesAfetadosFilter(e.target.value?.trimStart() || "")}
						sx={{ width: { xs: "100%", sm: 260 } }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search />
								</InputAdornment>
							),
							endAdornment: clientesAfetadosFilter ? (
								<InputAdornment position="end">
									<IconButton onClick={() => setClientesAfetadosFilter("")} edge="end">
										<Clear fontSize="small" />
									</IconButton>
								</InputAdornment>
							) : null,
						}}
					/>
				</Box>
			</Box>
			<Paper
				variant="outlined"
				elevation={3}
				sx={{
					width: "100%",
					overflow: "hidden",
					backgroundColor: theme.palette.background.paper,
					borderRadius: "16px",
				}}
			>
				<DatagridComponent
					rows={clientesAfetadosRows}
					columns={clientesAfetadosColumns}
					loading={loadingClientesAfetados}
					isPagination={true}
					// Linha de 40px (o padrão do componente força 28px com !important)
					// para o selo do ERP e o switch não serem cortados.
					sxDatagrid={{ border: 0, height: 440, "& .MuiDataGrid-row": { minHeight: "40px !important", maxHeight: "40px !important" } }}
					options={{ autoHeight: false, getRowHeight: () => 40 }}
				/>
			</Paper>

			<Divider sx={{ my: 3 }} />

			{/* --- OBSERVAÇÕES --- */}
			<Box sx={{ ...styles.boxContent, mb: 2 }}>
				<EditNoteOutlined color="primary" />
				<Typography variant="mySubtitle2">Observações Finais</Typography>
			</Box>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<TextField fullWidth multiline rows={4} label="Descreva as observações" name="observacao" value={formData.observacao} onChange={handleChange} />
				</Grid>
			</Grid>
			<Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
				<Box sx={styles.paperModalEditCheckBoxLabel}>
					<Box sx={styles.paperModalEditCheckBoxStickHeader}>
						<TextField autoComplete="off" fullWidth size="small" placeholder="Buscar ou criar" value={searchTerm} onChange={(e) => { e.stopPropagation(); e.preventDefault(); setSearchTerm(e.target.value); }} />
						<Divider />
					</Box>
					{searchTerm && !exactMatch && (
						<MenuItem onClick={() => handleAddCeckbox(searchTerm)}>
							<Add sx={{ mr: 1 }} fontSize="small" />
							<Box sx={styles.editCheckBoxTexts}>Criar "{searchTerm}"</Box>
						</MenuItem>
					)}
					{Array.isArray(filteredDeliveryTypes) && filteredDeliveryTypes?.map((type, index) => (
						<MenuItem key={index} sx={styles.boxFieldWithConfig}>
							<Box sx={styles.editCheckBoxTexts}>{type?.label}</Box>
							<IconButton size="small" onClick={(e) => { handleActionMenuClick(e, index); setSelectedFieldItem({ ...type, index: index }); }}>
								<MoreVert fontSize="small" />
							</IconButton>
						</MenuItem>
					))}
					<Menu
						anchorEl={anchorActionMenu}
						open={Boolean(anchorActionMenu)}
						onClose={handleActionMenuClose}
					>
						<MenuItem onClick={handleOpenEditModal}>
							<ListItem>
								<Edit fontSize="small" />
							</ListItem>
							Editar
						</MenuItem>
						<MenuItem
							onClick={() => {
								setOpenDeleteModal(true);
								handleActionMenuClose();
							}}
							sx={{ color: "error.main" }}
						>
							<ListItem sx={{ color: "error.main" }}>
								<Delete fontSize="small" />
							</ListItem>
							Excluir
						</MenuItem>
					</Menu>
				</Box>
			</Menu>
			<ModalEditCheckbox
				open={editModalOpen}
				onClose={() => setEditModalOpen(false)}
				onSave={handleSaveEditCheckbox}
				title="Editar tipo de entrega"
				value={editedLabel}
				onChange={(e) => setEditedLabel(e.target.value)}
			/>
			<InternoModalConfirmacaoSaida
				openConfirmacao={openDeleteModal}
				deleteLoad={loading}
				setOpenConfirmacao={setOpenDeleteModal}
				execute={handleRemoveCheckbox}
				title={"Atenção"}
				severity={"error"}
				contentText={
					"Esta ação afetará TODOS os clientes. Se você prosseguir com a operação, o item será deletado permanentemente!"
				}
				confirmText={"Excluir"}
			/>
		</Box >
	);
})